// Internal Imports
import { API_ENDPOINTS, DOCUMENTS } from "../../constants";
import {
  get,
  patch,
  put,
  post,
  deleteApi,
  uploadFile,
  getForFileAsResponse,
} from "../../api/index";
import store from "../../store";
import {
  getContentForComponent,
  getComponentPosition,
  getTableDBContent,
} from "../../utils/documentUtils";
import {
  updateDocSaveIndicator,
  updateComponentsDBRowColIndexesAction,
} from "./method";
import { readDataFromLocalStorage } from "../../utils/globalUtils";

// Destructuring
const { APIS } = API_ENDPOINTS;

const { DOCUMENT_MODE } = DOCUMENTS;
/*
========================================
    Document Details Functions
========================================
*/

export const uploadDocument = (filevalue, opp) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("doc", filevalue);
    formData.append("opp_name", opp);
    uploadFile(`${APIS.UPLOAD_DOCUMENT}`, formData)
      .then(res => {
        if (res.URI === undefined) {
          reject(new Error("Invalid response after uploading the document"));
        }
        resolve(res.URI);
      })
      .catch(error => {
        reject(error);
      });
  });

export const uploadPDFDocument = data =>
  new Promise((resolve, reject) => {
    const state = store.getState();
    const { assetStatusList } = state.AssetStore;

    const formData = new FormData();
    formData.append("file", data.data);
    formData.append("folder_id", data.folderId);
    formData.append("document_status", JSON.stringify(assetStatusList[0]));

    uploadFile(`${APIS.UPLOAD_PDF}`, formData)
      .then(res => {
        if (res && !res.Document && !res.Document.doc_no) {
          reject(new Error("Invalid response after uploading the document"));
        }
        resolve(res.Document.doc_no);
      })
      .catch(error => {
        reject(error);
      });
  });

export const createNewDocument = document =>
  new Promise((resolve, reject) => {
    post(`${APIS.CREATE_NEW_DOCUMENT}`, document)
      .then(res => {
        if (res.Document === undefined) {
          reject(new Error("Invalid response. 'Document' key is necessary"));
        }
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const deleteDocument = docID =>
  new Promise((resolve, reject) => {
    console.debug(APIS.DOCS);
    deleteApi(`${APIS.DOCS}/${docID}`)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const addDocumentComponentService = (docID, component) =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    post(`${APIS.DOCUMENTS_V2}/${docID}/components`, component)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        if (Array.isArray(res) && res.length > 0) {
          store.dispatch(updateComponentsDBRowColIndexesAction(res));
        }
        resolve(res);
      })
      .catch(err => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(err);
      });
  });

export const deleteDealtableFromDocument = dealtableID =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    const url = `${APIS.DEALTABLES}/${dealtableID}`;
    deleteApi(url)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        if (Array.isArray(res) && res.length > 0) {
          store.dispatch(updateComponentsDBRowColIndexesAction(res));
        }
        resolve(res);
      })
      .catch(err => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(err);
      });
  });

export const deleteDocumentComponentService = (docID, component) =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));

    const componentURL = `${APIS.DOCUMENTS_V2}/${docID}/components/${
      component.id || component.component_id
    }`;
    if (component.deleteDealTable && component.dealTableId) {
      deleteDealtableFromDocument(component.dealTableId)
        .then(res => {
          deleteApi(componentURL)
            .then(resposne => {
              resolve(resposne);
            })
            .catch(err => {
              reject(err);
            });
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    } else {
      deleteApi(componentURL)
        .then(res => {
          store.dispatch(updateDocSaveIndicator("SAVED"));
          if (Array.isArray(res) && res.length > 0) {
            store.dispatch(updateComponentsDBRowColIndexesAction(res));
          }
          resolve(res);
        })
        .catch(err => {
          store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
          reject(err);
        });
    }
  });

export const updateDocumentComponentService = section =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    const state = store.getState().DocumentStore;
    const {
      document: { templateID, fields_used: allDynamicFields },
    } = state;
    let documentType = DOCUMENT_MODE.DOCUMENT;
    if (templateID) {
      documentType = DOCUMENT_MODE.TEMPLATE_DOCUMENT;
    }
    const newComponent = getContentForComponent(section, documentType);
    const sectionID = section.id || section.section_id;
    newComponent.section_id = sectionID;
    newComponent.section_type = section.type || section.section_type;
    newComponent.section_name = section.header || section.section_name;
    newComponent.source = "DRAFT-JS";

    // get fields list
    const fields = [];
    const { clauseOptions } = newComponent;
    if (clauseOptions) {
      const { id, value } = clauseOptions;
      let fieldValue;
      const clauseOptionsDetails = allDynamicFields.find(
        clause => clause.id === id && clause.type !== "TABLE_API",
      );
      if (clauseOptionsDetails) {
        const {
          meta: { clauses },
        } = clauseOptionsDetails;
        if (Array.isArray(value)) {
          fieldValue = value.map(optionID =>
            clauses.find(c => c.id === optionID),
          );
        } else if (Array.isArray(clauses)) {
          fieldValue = clauses.find(c => c.id === value);
        }
        // if field value is blank, it is from free text. Create value pair
        if (!fieldValue) {
          fieldValue = {
            value,
          };
        }
        fields.push({ id, value: fieldValue });
      }
    }
    const updateComponentDetails = {
      component: {
        content: newComponent,
      },
      fields,
    };

    const componentURL = `${APIS.COMPONENTS}/${sectionID}`;
    put(componentURL, updateComponentDetails)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(res.Component.component);
      })
      .catch(err => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(err);
      });
  });

export const restoreDocumentComponentService = (docNo, component, layout) => {
  const { rowIndex, colIndex } = getComponentPosition(component.id, layout);
  if (!(rowIndex === undefined || colIndex === undefined)) {
    const componentPayload = {
      component: {
        content: getContentForComponent(component, DOCUMENT_MODE.DOCUMENT),
        row: rowIndex,
        col: colIndex,
        component_id: component.id,
      },
      new_row_added: layout[rowIndex].length === 1,
    };
    return new Promise((resolve, reject) => {
      put(`${APIS.DOCUMENTS_V2}/${docNo}/component_restore`, componentPayload)
        .then(res => {
          if (Array.isArray(res) && res.length > 0) {
            store.dispatch(updateComponentsDBRowColIndexesAction(res));
          }
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
  return null;
};

export const fetchClausesForDealTable = dealId =>
  new Promise((resolve, reject) => {
    get(`${APIS.DEALTABLES}/${dealId}`)
      .then(response => {
        if (!response.Clauses) {
          reject(Error("Invalid response. Clauses is a necessary key"));
        }
        resolve(response.Clauses);
      })
      .catch(error => {
        reject(error);
      });
  });

export const updateDocumentDetails = () =>
  new Promise((resolve, reject) => {
    const state = store.getState();
    const { document, content, document_order: docOrder } = state.DocumentStore;
    if (document === undefined || content === undefined) {
      reject(Error("No document available"));
    }

    const { id } = document;
    const contentDetails = {
      order: docOrder,
    };

    post(`${APIS.COMPONENTS}/documents/${id}/`, contentDetails)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const updateDocumentOrder = payload =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    const state = store.getState();
    const { document, components } = state.DocumentStore;

    if (!(document && payload)) {
      reject(Error("No document available, or incorrect order"));
    }

    if (!payload.old_loc) {
      const component = components[payload.component.id];
      payload = {
        ...payload,
        old_loc: {
          row: component.dbRowIndex,
          col: component.dbColIndex,
        },
      };
    }

    put(`${APIS.DOCUMENTS_V2}/${document.docNo}/component_layout`, payload)
      .then(res => {
        if (Array.isArray(res) && res.length > 0) {
          store.dispatch(updateComponentsDBRowColIndexesAction(res));
        }
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(payload.old_loc);
      })
      .catch(error => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(error);
      });
  });

export const fetchAllDocumentTypes = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.DOC_TYPES}`)
      .then(res => {
        if (res.DocumentTypes !== undefined) {
          resolve(res.DocumentTypes);
        } else {
          reject(Error("Error while fetching doc types"));
        }
      })
      .catch(error => {
        reject(error);
      });
  });

/* 
  Assets Library Services below
*/

/* 
  Document Approval Services below
*/

export const analyzeDocService = payload =>
  new Promise((resolve, reject) => {
    post(`${APIS.WORK_FLOW_EVENT}`, payload)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const sendDocForApprovalService = payload =>
  new Promise((resolve, reject) => {
    post(`${APIS.WORK_FLOW_TRIGGER}`, payload)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const approvalStatusCheckService = (docID, status) =>
  new Promise((resolve, reject) => {
    get(`${APIS.APPROVALS}/deal/${docID}?approval_status=${status || ""}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const approveRejectDocService = (docID, payload, isApproved) =>
  new Promise((resolve, reject) => {
    post(
      `${APIS.APPROVALS}/deal/${docID}/${isApproved ? "approve" : "reject"}`,
      payload,
    )
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

// The doc should be fully approved to use this
export const resetDocToDraftService = docID =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_STAGE}/${docID}/draft`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const forceApproveDocService = docID =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_STAGE}/${docID}/approved`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const changeDocumentStatusService = (docID, changedStatus) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_STATUS}/${docID}/${changedStatus}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

/* 
  Document Approval Services above
*/

export const updateMultipleDocumentSections = async sectionList => {
  try {
    const notesPromises = [];
    sectionList.forEach(section =>
      notesPromises.push(updateDocumentComponentService(section)),
    );
    return await Promise.all(notesPromises);
  } catch (error) {
    throw error;
  }
};
export const fetchDynamicFieldsService = () =>
  new Promise((resolve, reject) => {
    get(APIS.DOC_FIELDS)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

// The doc should be fully approved to use this
export const editApprovedDocumentService = (docID, sectionList) =>
  new Promise((resolve, reject) => {
    post(`${APIS.EDIT_DOCUMENT}/${docID}`, sectionList)
      .then(response => {
        if (!response.Document) {
          reject(Error("Invalid response. Document is a necessary key"));
        }
        resolve(response.Document);
      })
      .catch(error => {
        reject(error);
      });
  });

export const sendDocForSignature = (id, data) =>
  new Promise((resolve, reject) => {
    post(`${APIS.SEND_DOC_TO_DOCU_SIGN}/${id}`, data)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const addAcceptorsService = (docID, data) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOCS_ACCEPTORS}/${docID}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const addDocRecipientsService = (docId, data) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOCS_RECIPIENTS_V2}/${docId}`, data)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        console.error(
          `DocumentActions => service => addDocRecipientsService => Error :${error}`,
        );
        reject(error);
      });
  });

export const fetchDocRecipientListService = docId =>
  new Promise((resolve, reject) => {
    get(`${APIS.DOCS_RECIPIENTS}/${docId}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        console.error(
          `DocumentActions => service => fetchDocRecipientListService => Error :${error}`,
        );
        reject(error);
      });
  });

export const legallyBindedSignatory = () =>
  new Promise((resolve, reject) => {
    get(APIS.LEGALLY_BINDED_SIGNATORY)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const updateDocumentFieldCatalog = (docId, fields, noDispatch) =>
  new Promise((resolve, reject) => {
    if (!noDispatch) {
      store.dispatch(updateDocSaveIndicator("SAVING"));
    }
    const reduxState = store.getState();
    const {
      document: { fields_used: fieldUsed },
      // components,
    } = reduxState.DocumentStore;
    const rawFieldvalue = [];

    if (Array.isArray(fields)) {
      fields.forEach(field => {
        const formField = fieldUsed.find(
          fieldDetail => field.id === fieldDetail.id,
        );
        if (formField && formField.type === "TABLE") {
          // const getValue =
          //   components[formField.occurrences[0].componentId].content.data;
          // const rawValue = getTableDBContent(getValue, DOCUMENT_MODE.DOCUMENT);
          // rawFieldvalue.push({ ...field, value: rawValue });
        } else {
          rawFieldvalue.push({ ...field });
        }
      });
    }
    const updateFieldURL = `${APIS.UPDATE_DOC_FIELD}/${docId}/fields/`;
    put(updateFieldURL, rawFieldvalue)
      .then(res => {
        if (!noDispatch) {
          store.dispatch(updateDocSaveIndicator("SAVED"));
        }
        resolve(res);
      })
      .catch(err => {
        if (!noDispatch) {
          store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        }
        reject(err);
      });
  });

export const updateDocumentField = (docId, fields, noDispatch) =>
  new Promise((resolve, reject) => {
    if (!noDispatch) {
      store.dispatch(updateDocSaveIndicator("SAVING"));
    }
    const reduxState = store.getState();
    const {
      document: { fields_used: fieldUsed },
    } = reduxState.DocumentStore;
    const rawFieldvalue = [];
    if (Array.isArray(fields)) {
      fields.forEach(field => {
        const formField = fieldUsed.find(
          fieldDetail => field.id === fieldDetail.id,
        );
        if (formField && formField.type === "TABLE") {
          const rawValue = getTableDBContent(
            field.value || formField.value,
            DOCUMENT_MODE.DOCUMENT,
          );
          rawFieldvalue.push({ ...field, value: rawValue });
        } else {
          rawFieldvalue.push({ ...field });
        }
      });
    }
    const updateFieldURL = `${APIS.UPDATE_DOC_FIELD}/${docId}/fields/`;
    put(updateFieldURL, rawFieldvalue)
      .then(res => {
        if (!noDispatch) {
          store.dispatch(updateDocSaveIndicator("SAVED"));
        }
        resolve(res);
      })
      .catch(err => {
        if (!noDispatch) {
          store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        }
        reject(err);
      });
  });

export const updateDocumentFillerType = (docId, data) =>
  new Promise((resolve, reject) => {
    put(`${APIS.UPDATE_DOC_FIELD}/${docId}/filler/`, data)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const updateDocumentRecipientField = (docId, fields, noDispatch) =>
  new Promise((resolve, reject) => {
    if (!noDispatch) {
      store.dispatch(updateDocSaveIndicator("SAVING"));
    }
    const updateFieldURL = `${APIS.UPDATE_DOC_FIELD}/${docId}/fields/`;
    put(updateFieldURL, fields)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const updateDocumentFieldNonLoggedIn = (docId, sharingHash, fields) =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    const updateFieldURL = `${APIS.UPDATE_DOC_FIELD_NON_LOGGED_IN}/${docId}/${sharingHash}/fields/`;
    put(updateFieldURL, fields)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(res);
      })
      .catch(err => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(err);
      });
  });

export const updateSubmitRejectSignerFiller = (
  docId,
  sharingHash,
  status,
  data,
) =>
  new Promise((resolve, reject) => {
    const updateFieldURL = `${APIS.UPDATE_DOC_FIELD_NON_LOGGED_IN}/${docId}/${sharingHash}/doc-fill/${status}`;
    post(updateFieldURL, data)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const batchUpdateDocumentSection = (sections, fields, docId) =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    const state = store.getState().DocumentStore;
    const { document } = state;
    const documentId = docId || (document && document.id); // Handling normal & doc creation scenario
    let documentType = DOCUMENT_MODE.DOCUMENT;
    if (document && document.templateID) {
      documentType = DOCUMENT_MODE.TEMPLATE_DOCUMENT;
    }
    const updatedComponents = sections.map(section => {
      const newComponent = getContentForComponent(section, documentType);
      const sectionID = section.section_id || section.id;
      newComponent.id = sectionID;
      newComponent.section_id = sectionID;
      newComponent.section_type = section.type || section.section.section_type;
      newComponent.section_name = section.header || section.section_name;
      newComponent.source = "DRAFT-JS";
      const updateComponentDetails = {
        content: newComponent,
        component_id: sectionID,
      };
      return updateComponentDetails;
    });

    const updateDetails = {
      components: updatedComponents,
      fields,
    };
    const componentURL = `${APIS.UPDATE_DOC_COMPONENT_BATCH}/${documentId}/`;
    put(componentURL, updateDetails)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(res);
      })
      .catch(err => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(err);
      });
  });

export const updateDocDetailsService = payload =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    patch(`${APIS.DOCS}/${payload.docId}`, payload.data)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(res.Document);
      })
      .catch(error => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        console.error(
          `DocumentActions => services => updateDocDetailsService => ${error}`,
        );
        reject(error);
      });
  });

export const sendDocumentForSignature = signaturePayload =>
  new Promise((resolve, reject) => {
    post(`${APIS.ESIGNATURE}`, signaturePayload)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `DocumentActions => services => sendDocumentForSignature => ${error}`,
        );
        reject(error);
      });
  });

export const cancleESignatureService = docId =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.CANCLE_ESIGNATURE}/${docId}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `DocumentActions => services => cancleESignatureService => ${error}`,
        );
        reject(error);
      });
  });

// changes from new asset page, document status and tags
export const updateDocumentStatusService = (docId, statusId) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_STATUS}/${docId}/${statusId}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const updateDocumentTagService = (docId, tagId) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_TAG}/${docId}/${tagId}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const docAccepAndSignService = (docId, sharinghash) =>
  new Promise((resolve, reject) => {
    post(`${APIS.SHARED_DOCUMENTS}/${docId}/${sharinghash}/signature`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(`documentServices => docAccepAndSignService => ${error}`);
        reject(error);
      });
  });

export const docAfterAccepAndSignService = (docId, sharinghash) =>
  new Promise((resolve, reject) => {
    get(`${APIS.SHARED_DOCUMENTS}/${docId}/${sharinghash}/signingurl`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `documentServices => docAfterAccepAndSignService => ${error}`,
        );
        reject(error);
      });
  });

export const deleteReviewersServices = (docId, reviewerID) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.DOCS_RECIPIENTS}/${docId}/reviewers/${reviewerID}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(`documentServices => deleteReviewers=> ${error}`);
        reject(error);
      });
  });
// Activity api calls
export const getDocActivities = (
  objectid,
  before = null,
  after = null,
  limit = null,
) => {
  let queryParam = "";
  if (before || after || limit) {
    queryParam = "?";
    if (before) {
      queryParam = `${queryParam}before=${before}&`;
    }
    if (after) {
      queryParam = `${queryParam}after=${after}&`;
    }
    if (limit) {
      queryParam = `${queryParam}limit=${limit}`;
    }
  }
  return new Promise((resolve, reject) => {
    get(`${APIS.ACTIVITIES}/${objectid}${queryParam}`, true)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(`documentServices => getDocActivies => ${error}`);
        reject(error);
      });
  });
};

export const editReviewerPermissionServices = (docId, reviewerID, data) =>
  new Promise((resolve, reject) => {
    put(`${APIS.DOCS_RECIPIENTS}/${docId}/reviewers/${reviewerID}`, {
      is_editable: data,
    })
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(`documentServices => deleteReviewers=> ${error}`);
        reject(error);
      });
  });

export const acceptanceApprovalStatusCheckService = (
  docID,
  sharingHash,
  status,
  reviewerEmailMessage,
  attachmentId,
) =>
  new Promise((resolve, reject) => {
    post(
      `${APIS.ACCEPTANCE_STATUS}/${docID}/${sharingHash}/doc-acceptance/${status}`,
      { attachment_id: attachmentId, user_response: reviewerEmailMessage },
    )
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `documentServices => acceptanceApproverStatus=> ${error}`,
        );
        reject(error);
      });
  });

export const generatePermaLinkService = payload =>
  new Promise((resolve, reject) => {
    post(`${APIS.PERMA_LINK}`, payload)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(`documentServices => generatePermaLink=> ${error}`);
        reject(error);
      });
  });

export const generateSnapLinkService = payload =>
  new Promise((resolve, reject) => {
    const blob = new Blob([payload.file], { type: "plain/html" });
    const formData = new FormData();
    formData.append("object_id", payload.object_id);
    formData.append("object_type", payload.object_type);
    formData.append("file", blob, payload.fileName);
    formData.append("css", payload.css);
    uploadFile(`${APIS.SNAP_LINK}`, formData)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(`documentServices => generateSnapLink=> ${error}`);
        reject(error);
      });
  });

export const getPermaLinkService = (objectid, objectType) =>
  new Promise((resolve, reject) => {
    get(`${APIS.PERMA_LINK}/${objectType}/${objectid}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(`documentServices => getPermaLink=> ${error}`);
        reject(error);
      });
  });

export const getSnapLinkService = (objectid, objectType) =>
  new Promise((resolve, reject) => {
    get(`${APIS.SNAP_LINK}/${objectType}/${objectid}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(`documentServices => getSnapLink=> ${error}`);
        reject(error);
      });
  });

export const deletePermaLinkService = (objectid, objectType) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.PERMA_LINK}/${objectType}/${objectid}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(`documentServices => deletePermaLink=> ${error}`);
        reject(error);
      });
  });

export const deleteSnapLinkService = (objectid, objectType) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.SNAP_LINK}/${objectType}/${objectid}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(`documentServices => deleteSnapLink=> ${error}`);
        reject(error);
      });
  });

export const cloneDocumentService = docId =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOCUMENTS}/${docId}/clone`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `cloneDocumentService => cloneDocumentService=> ${error}`,
        );
        reject(error);
      });
  });

export const uploadImageService = filevalue =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("image", filevalue);
    uploadFile(`${APIS.IMAGE_UPLOAD}`, formData)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(error);
        reject(error);
      });
  });

export const documentAttachmentService = (
  docId,
  filevalue,
  thumbnailValue,
  sharingHash,
) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("attachment", filevalue);
    if (thumbnailValue) {
      formData.append("thumbnail", thumbnailValue);
    }
    if (sharingHash) {
      uploadFile(
        `${APIS.SHARE_DOCUMENT_ATTACHMENT}/documents/${docId}/${sharingHash}`,
        formData,
      )
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    } else {
      uploadFile(`${APIS.DOC_ATTACHMENTS}/documents/${docId}`, formData)
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    }
  });

export const fetchDocumentAttachmentsService = (docId, sharingHash) =>
  new Promise((resolve, reject) => {
    if (sharingHash) {
      get(`${APIS.SHARE_DOCUMENT_ATTACHMENT}/documents/${docId}/${sharingHash}`)
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    } else {
      get(`${APIS.DOC_ATTACHMENTS}/documents/${docId}`)
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    }
  });

export const uploadSharedDocumentAttachmentService = (
  docId,
  sharingHash,
  filevalue,
) =>
  new Promise((resolve, reject) => {
    if (sharingHash) {
      const formData = new FormData();
      formData.append("attachment", filevalue);
      post(
        `${APIS.SHARE_DOCUMENT_ATTACHMENT}/documents/${docId}/${sharingHash}`,
        formData,
      )
        .then(response => {
          resolve(response.Attachment);
        })
        .catch(error => {
          console.error(`DocumentActions => services => Error :`, error);
          reject(error);
        });
    }
  });

export const uploadSharedMasterDocumentAttachmentService = (
  docName,
  sharingHash,
  filevalue,
) =>
  new Promise((resolve, reject) => {
    if (sharingHash) {
      const formData = new FormData();
      formData.append("attachment", filevalue);
      post(`${APIS.ATTACHMENTS_MASTER_DOC}/${sharingHash}/${docName}`, formData)
        .then(response => {
          resolve(response.Attachment);
        })
        .catch(error => {
          console.error(
            `uploadSharedMasterDocumentAttachmentService => services => Error :`,
            error,
          );
          reject(error);
        });
    }
  });

export const removeAttachmentService = (attachmentId, docId) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.DOC_ATTACHMENTS}/${attachmentId}/documents/${docId}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const updateDocSignatories = (signerData, docId) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_SIGNATORY}/${docId}`, signerData)
      .then(response => resolve(response))
      .catch(error => reject(error));
  });

export const deleteDocSigner = (signerData, docId) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.DOC_SIGNATORY}/${docId}`, signerData)
      .then(response => resolve(response))
      .catch(error => reject(error));
  });

export const updateSubmitDeclineInternalService = (docId, type, data) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOCUMENTS}/${docId}/doc-fill/${type}`, data)
      .then(response => {
        resolve(response);
      })
      .catch(err => {
        reject(err);
      });
  });

export const getStripeUser = () =>
  new Promise((resolve, reject) => {
    get(
      `${APIS.STRIPE_AUTH}/subscriber/${
        readDataFromLocalStorage("revvsalesUser").id
      }`,
    )
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const authStripe = configData =>
  new Promise((resolve, reject) => {
    post(`${APIS.STRIPE_AUTH}/subscriber`, configData)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const sendStripePaymentOnSave = content =>
  new Promise((resolve, reject) => {
    post(`${APIS.STRIPE_AUTH}/payments`, content)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getStripePaymentSessionId = content =>
  new Promise((resolve, reject) => {
    post(`${APIS.STRIPE_AUTH}/checkout`, content)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getStripePaymentDetails = (docId, stripeID) =>
  new Promise((resolve, reject) => {
    get(`${APIS.STRIPE_AUTH}/payments/${docId}/${stripeID}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getStripePaymentDetailsWithHash = (docId, userID, sharingHash) =>
  new Promise((resolve, reject) => {
    get(`${APIS.STRIPE_AUTH}/shared/${docId}/${userID}/${sharingHash}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const deactivateStripe = stripeID =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.STRIPE_AUTH}/subscriber/${stripeID}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getStripePaymentDetailsWithHashPerma = (
  docId,
  userID,
  sharingHashPermaLink,
) =>
  new Promise((resolve, reject) => {
    get(`${APIS.STRIPE_AUTH}/perma/${docId}/${userID}/${sharingHashPermaLink}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const deleteStripeFromDocument = (stripeData, docId) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.STRIPE_AUTH}/doc-buttons/${docId}`, stripeData)
      .then(response => resolve(response))
      .catch(error => reject(error));
  });

export const getEvidenceSummary = docId =>
  new Promise((resolve, reject) => {
    getForFileAsResponse(`${APIS.DOCS}/${docId}/esign-evidence`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const updateEmailResendService = (docId, payload) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOCS_RECIPIENTS}/${docId}/resend-email`, payload)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getMasterDocDataService = objectid =>
  new Promise((resolve, reject) => {
    get(`${APIS.MASTER_DOC_LINK}/DOC/${objectid}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `documentServices => getMasterDocDataService => ${error}`,
        );
        reject(error);
      });
  });

export const updatePDFFormFieldServiceDocument = (docId, updatedFields) =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    post(`${APIS.PDF_FORM_FIELDS}/${docId}`, updatedFields)
      .then(response => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(response);
      })
      .catch(error => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        console.debug(
          `assetServices => getAssetFolderTreeService => Error : ${error}`,
        );
        reject(error);
      });
  });

export const generateMasterDocumentService = payload =>
  new Promise((resolve, reject) => {
    post(`${APIS.MASTER}`, payload)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `documentServices => generateMasterDocumentService => ${error}`,
        );
        reject(error);
      });
  });

export const disableMasterDocumentService = objectid =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.MASTER_DOC_LINK}/DOC/${objectid}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `documentServices => disableMasterDocumentService => ${error}`,
        );
        reject(error);
      });
  });

export const createBulkDocumentService = (templateID, data, payload) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("doc", data);
    uploadFile(`${APIS.DOCS_BULK_CREATE}${templateID}${payload}`, formData)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `documentServices => disableMasterDocumentService => ${error}`,
        );
        reject(error);
      });
  });

export const getComponentFromStore = componentID =>
  new Promise(resolve => {
    const state = store.getState().DocumentStore;
    const { components } = state;
    resolve(components[componentID]);
  });

export const getPageBreakScrollToView = () =>
  new Promise(resolve => {
    const state = store.getState().DocumentStore;
    const { pageBreakScrollToView } = state;
    resolve(pageBreakScrollToView);
  });

export const getFileDataWithFileLink = fileLink =>
  new Promise((resolve, reject) => {
    getForFileAsResponse(fileLink)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getDocumentSettingsActionService = docId =>
  new Promise((resolve, reject) => {
    get(`${APIS.DOC_SETTINGS}/${docId}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `documentServices => getMasterDocDataService => ${error}`,
        );
        reject(error);
      });
  });

export const updatedDocumentSettingsActionService = (docId, data) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_SETTINGS}/${docId}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `documentServices => updatedDocumentSettingsActionService => ${error}`,
        );
        reject(error);
      });
  });

export const addSharedDocRecipientsActionService = (docId, sharinghash, data) =>
  new Promise((resolve, reject) => {
    post(
      `${APIS.ACCEPTANCE_STATUS}/${docId}/${sharinghash}/doc-recipients`,
      data,
    )
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `documentServices => addSharedDocRecipientsActionService => ${error}`,
        );
        reject(error);
      });
  });

// ------- Esign APIs -------
export const getDocSignatoriesV2 = docID =>
  new Promise((resolve, reject) => {
    get(`${APIS.DOC_SIGNATORIES_V2}${docID}`)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const createDocSignatoryV2 = (docID, data) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_SIGNATORIES_V2}${docID}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const createDocSignerIdentity = docID =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_SIGNER_IDS}${docID}`, {})
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const getDocSignerIdentities = docID =>
  new Promise((resolve, reject) => {
    get(`${APIS.DOC_SIGNER_IDS}${docID}`)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const updateDocSignerIdentity = (docID, signerID, data) =>
  new Promise((resolve, reject) => {
    put(`${APIS.DOC_SIGNER_IDS}${docID}/${signerID}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const deleteDocSignerIdentity = (docID, signerID, data = {}) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.DOC_SIGNER_IDS}${docID}/${signerID}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

// bulk components delete - for esign(can be used for any)
export const deleteComponentsBulk = (docID, data) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.DOCUMENTS}/${docID}/bulk-components`, data)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });
// ------ End Esign APIs --------
// ---- ESIGN TEMPLATE -----------

export const getTemplateSignatoriesV2 = templateID =>
  new Promise((resolve, reject) => {
    get(`${APIS.TEMPLATE_SIGNATORY_V2}${templateID}`)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const createTemplateSignatoryV2 = (templateID, data) =>
  new Promise((resolve, reject) => {
    post(`${APIS.TEMPLATE_SIGNATORY_V2}${templateID}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const createTemplateSignerIdentity = templateID =>
  new Promise((resolve, reject) => {
    post(`${APIS.TEMPLATE_SIGNER_IDENTITIES}${templateID}`, {})
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const getTemplateSignerIdentities = templateID =>
  new Promise((resolve, reject) => {
    get(`${APIS.TEMPLATE_SIGNER_IDENTITIES}${templateID}`)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const updateTemplateSignerIdentity = (templateID, signerID, data) =>
  new Promise((resolve, reject) => {
    put(`${APIS.TEMPLATE_SIGNER_IDENTITIES}${templateID}/${signerID}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const deleteTemplateSignerIdentity = (templateID, signerID, data = {}) =>
  new Promise((resolve, reject) => {
    deleteApi(
      `${APIS.TEMPLATE_SIGNER_IDENTITIES}${templateID}/${signerID}`,
      data,
    )
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const deleteTemplateComponent = (templateID, data) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.TEMPLATE_V2}/${templateID}/components`, data)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const convertFormDoctoEditor = docId =>
  new Promise((resolve, reject) => {
    put(`${APIS.CONVERT_FORMDOC_TO_EDITOR}/${docId}`)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });
// ------ END ESIGN TEMPLATE APIS -----
